import { UniversalFormProvider } from '@shared/components';
import { DeleteDialog } from '@retail/components';
import { useCalculusTFunction } from '@retail/calculus/i18n';
import {
  usePermissionsForCurrentUser,
  useSelectedOrgUnit,
} from '@retail/app/stores/selected-context';
import { activateAgreementSchema } from '@retail/purchase-agreements/components';
import { useAgreements } from '@retail/purchase-agreements/context';
import { ConditionsNew } from '@retail/calculus/types';
import { useConditionActions } from '@retail/calculus/context';
import { CreatePurchaseCondition } from './Create';
import { EditAgreementConditionDialog } from './Edit';

interface Props {
  supplierNumber: number;
  createCondition: boolean;
  conditions: ConditionsNew[];
  editCondition?: ConditionsNew;
  deactivateConditionId?: number;
  deleteConditionId?: number;
  closeCreate: () => void;
  closeEdit: () => void;
  closeDeactivate: () => void;
  closeDelete: () => void;
}

export function PurchaseConditionActions({
  supplierNumber,
  createCondition,
  conditions,
  editCondition,
  deactivateConditionId,
  deleteConditionId,
  closeCreate,
  closeEdit,
  closeDeactivate,
  closeDelete,
}: Props) {
  const t = useCalculusTFunction();
  const { assortmentId } = useSelectedOrgUnit();

  const { removeCondition } = useConditionActions();
  const { agreements } = useAgreements({ supplierNumber });

  const { userCanCreate, userCanUpdate } = usePermissionsForCurrentUser();
  const canCreatePurchaseConditions = userCanCreate('PRICE_CALCULATION__PURCHASE_CONDITION');
  const canUsePurchaseConditionActions = userCanUpdate('PRICE_CALCULATION__PURCHASE_CONDITION');

  return (
    <>
      {createCondition && canCreatePurchaseConditions && (
        <UniversalFormProvider
          schema={activateAgreementSchema(t)}
          defaultValues={activateAgreementSchema(t).getDefault()}
        >
          <CreatePurchaseCondition
            open
            agreements={agreements}
            conditions={conditions}
            assortmentId={assortmentId}
            onClose={closeCreate}
            supplierNumber={supplierNumber}
          />
        </UniversalFormProvider>
      )}
      {editCondition && canUsePurchaseConditionActions && (
        <EditAgreementConditionDialog
          open
          onClose={closeEdit}
          assortmentId={assortmentId}
          agreements={agreements}
          editCondition={editCondition}
        />
      )}
      {deactivateConditionId && canUsePurchaseConditionActions && (
        <DeleteDialog
          isOpen
          dialogTitle={t('calculus.actions.deactivateCondition')}
          message={t('calculus.actions.confirmDeactivation')}
          onDelete={() => removeCondition([deactivateConditionId], 'deactivate')}
          onClose={closeDeactivate}
        />
      )}
      {deleteConditionId && canUsePurchaseConditionActions && (
        <DeleteDialog
          isOpen
          dialogTitle={t('calculus.actions.deleteFromDraft')}
          message={t('calculus.actions.confirmRemoval')}
          onDelete={() => removeCondition([deleteConditionId], 'delete')}
          onClose={closeDelete}
        />
      )}
    </>
  );
}
